// src/utils/trendDataMerge.ts
//
// A table's trend series on the dashboard (plan 2026-10-16 table sparkline,
// D3/D4). The refresh answers it BESIDE the spec, as the response field
// `trend_data`, the way it answers `comparison_data`: the series belongs to
// a window that moves every period, so it is never stored (ADR-0422; the
// backend strips it from a spec an editor re-saves, `RESPONSE_ONLY_SPEC_KEYS`).
// The report resolver stamps the same payload ON its spec, so the table model
// reads `spec.trend_data` on both surfaces; this is the dashboard's half of
// that contract, applied wherever a refresh response (live or from the
// browser cache) becomes the spec a tile draws.

type TrendCarrier = {
  chart_spec_json?: Record<string, unknown> | null;
  trend_data?: Record<string, unknown> | null;
};

/** The table spec with the response's trend series on it. A spec without a
 *  `trend_column` is returned as it came: it draws no column, and a stray
 *  series would only sit in memory. */
export function specWithTrendData(response: TrendCarrier): Record<string, unknown> {
  const spec = (response.chart_spec_json ?? {}) as Record<string, unknown>;
  if (spec.trend_column == null || response.trend_data == null) return spec;
  return { ...spec, trend_data: response.trend_data };
}

/** Does a cached refresh answer lack the trend its table asks for? A tile
 *  hydrated from the dashboard GET, a tab snapshot or a prewarmed range
 *  carries no `trend_data` (only a refresh computes it), and a cache hit
 *  would draw that answer with a skeleton nothing ever replaces. Such an
 *  entry is a MISS, so the tile asks the refresh, whose own server-side
 *  cache answers cheaply. */
export function lacksTrendData(response: TrendCarrier): boolean {
  const spec = response.chart_spec_json;
  return spec != null && spec.trend_column != null && response.trend_data == null && spec.trend_data == null;
}
